import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { IonAccordionGroup } from '@ionic/angular';
import { FilterOption } from '@shared/components/filter-modal/filter-modal.component';
import { ChipTypeEnum } from '@app/shared-product/components/usf-chip/models/chip-type.enum';
import { PlatformEnum } from '@shared/constants/platform-type.enum';
import { SortSelection } from '@shared/models/sort.model';
import {
  ListConstants,
  ListSearchSortFilterOptions,
  PmeFilterOptions,
  PmeSortOptions,
} from '@usf/ngrx-list';

@Component({
  selector: 'app-pme-sort-filter',
  templateUrl: './pme-sort-filter.component.html',
  styleUrls: ['./pme-sort-filter.component.scss'],
})
export class PmeSortFilterComponent implements OnInit, OnChanges {
  @ViewChild('accordionGroup', { static: true })
  accordionGroup!: IonAccordionGroup;

  @Input() sortFilterOptions: ListSearchSortFilterOptions = {
    searchKey: '',
    groupToSort: ListConstants.allGroups,
    sortType: ListConstants.groupLine,
    sortDirection: ListConstants.asc,
    filters: [],
  };
  @Input() platformType: PlatformEnum;
  @Input() isRecentlyPurchased: boolean;
  @Input() sgpSort: boolean;
  @Input() hideLowLimit: boolean;

  @Output() closeEmitter = new EventEmitter<void>();
  @Output() saveEmitter = new EventEmitter<ListSearchSortFilterOptions>();
  listConstants = ListConstants;
  platformRef = PlatformEnum;
  chipTypeEnum = ChipTypeEnum;

  sortButtons: SortSelection[] = [
    {
      fieldName: 'i18n.lists.pmeList.gpd',
      sortValue: PmeSortOptions.gpd,
    },
    {
      fieldName: 'i18n.lists.marketChange',
      sortValue: PmeSortOptions.marketChange,
    },
    {
      fieldName: 'i18n.productDetail.customPricingRationaleModal.priceChange',
      sortValue: PmeSortOptions.priceChange,
    },
    {
      fieldName: 'i18n.lists.pmeList.wwp2',
      sortValue: PmeSortOptions.wwp,
    },
  ];

  private allFilterButtons: FilterOption[] = [
    {
      id: PmeFilterOptions.cprAutoAssist,
      label: 'i18n.lists.pmeList.cprAutoAssist',
      selected: false,
    },
    {
      id: PmeFilterOptions.noCprEstablished,
      label: 'i18n.lists.pmeList.noCprEstablished',
      selected: false,
    },
    {
      id: PmeFilterOptions.inFlrAndTgtRange,
      label: 'i18n.lists.pmeList.inFlrAndTgtRange',
      selected: false,
    },
    {
      id: PmeFilterOptions.manualCpr,
      label: 'i18n.lists.pmeList.manualCpr',
      selected: false,
    },
    {
      id: PmeFilterOptions.showAllPriceChange,
      label: 'i18n.lists.pmeList.showAllPriceChanged',
      selected: false,
    },
    {
      id: PmeFilterOptions.notInOptimizedRange,
      label: 'i18n.lists.pmeList.notInOptimizedRange',
      selected: false,
    },
    {
      id: PmeFilterOptions.showAllMarketChange,
      label: 'i18n.lists.pmeList.showAllMarketChanged',
      selected: false,
    },
  ];

  filterButtons: FilterOption[] = [];

  private cleanFiltersBasedOnFeatureFlags(filters: string[]): string[] {
    return filters.filter(filter => {
      if (filter === PmeFilterOptions.inFlrAndTgtRange) {
        return !this.hideLowLimit;
      }
      return true;
    });
  }

  ngOnInit(): void {
    this.updateFilterButtons();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sgpSort']) {
      this.updateSortButtons();
    }
    if (changes['hideLowLimit']) {
      this.updateFilterButtons();
    }
  }

  private updateFilterButtons(): void {
    this.filterButtons = this.allFilterButtons.filter(filter => {
      if (filter.id === PmeFilterOptions.inFlrAndTgtRange) {
        return !this.hideLowLimit;
      }
      return true;
    });
  }

  private updateSortButtons(): void {
    this.sortButtons[0].fieldName = this.sgpSort
      ? 'i18n.lists.pmeList.sgp'
      : 'i18n.lists.pmeList.gpd';
  }

  selectSort(sortOption: string) {
    let newSortFilterOptions: ListSearchSortFilterOptions = {
      ...this.sortFilterOptions,
      filters: this.sortFilterOptions.filters.slice(),
    };
    if (this.sortFilterOptions?.sortType !== sortOption) {
      newSortFilterOptions.sortType = sortOption;
      newSortFilterOptions.sortDirection = this.listConstants.asc;
    } else {
      newSortFilterOptions.sortDirection =
        this.sortFilterOptions.sortDirection === this.listConstants.asc
          ? this.listConstants.desc
          : this.listConstants.asc;
    }
    newSortFilterOptions.filters = this.cleanFiltersBasedOnFeatureFlags(
      newSortFilterOptions.filters,
    );
    this.saveEmitter.emit(newSortFilterOptions);
  }

  selectFilter(filter: string, event?: any) {
    let newSortFilterOptions: ListSearchSortFilterOptions = {
      ...this.sortFilterOptions,
      filters: this.sortFilterOptions.filters.slice(),
    };
    if (event) {
      event.stopPropagation();
    }
    if (!newSortFilterOptions?.filters?.includes(filter)) {
      newSortFilterOptions.filters.push(filter);
    } else {
      newSortFilterOptions.filters = newSortFilterOptions.filters.filter(
        currentFilter => currentFilter !== filter,
      );
    }
    newSortFilterOptions.filters = this.cleanFiltersBasedOnFeatureFlags(
      newSortFilterOptions.filters,
    );
    this.saveEmitter.emit(newSortFilterOptions);
  }

  getSortText(sort: string) {
    return this.sortButtons.find(button => button.sortValue === sort)
      ?.fieldName;
  }

  getFilterText(filter: string) {
    return this.filterButtons.find(button => button.id === filter)?.label;
  }

  getSortDirection(direction: string) {
    return direction === this.listConstants.asc
      ? 'i18n.common.sortAscendingCut'
      : 'i18n.common.sortDescendingCut';
  }

  closeSortFilterMenu() {
    this.closeEmitter.emit();
  }

  clearSort() {
    let newSortFilterOptions: ListSearchSortFilterOptions = {
      ...this.sortFilterOptions,
      filters: this.sortFilterOptions.filters.slice(),
    };
    if (this.isRecentlyPurchased) {
      newSortFilterOptions.sortType = ListConstants.date;
    } else {
      newSortFilterOptions.sortType = ListConstants.groupLine;
    }
    newSortFilterOptions.sortDirection = this.listConstants.asc;
    newSortFilterOptions.filters = this.cleanFiltersBasedOnFeatureFlags(
      newSortFilterOptions.filters,
    );
    this.saveEmitter.emit(newSortFilterOptions);
  }

  clearAll() {
    const newSortFilterOptions: ListSearchSortFilterOptions = {
      searchKey: '',
      groupToSort: ListConstants.allGroups,
      sortType: this.isRecentlyPurchased
        ? ListConstants.date
        : ListConstants.groupLine,
      sortDirection: ListConstants.asc,
      filters: [],
    };
    this.saveEmitter.emit(newSortFilterOptions);
  }
}
